import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Table, Tag, Button, Card, Select, Space, Typography,
  Tooltip, message, Alert, Statistic, Row, Col, Progress, Input, Switch,
} from 'antd';
import {
  SyncOutlined, DownloadOutlined, FilePdfOutlined, SearchOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { getAgentAuthHeaders } from '../../services/api/settingsApi';

const { Text } = Typography;
const AGENT_URL = import.meta.env.VITE_CSG_GATEWAY_URL || 'http://localhost:3000';

// ── API helpers ──────────────────────────────────────────────────────────────

async function fetchSophosEmailSyncResults(classification) {
  const params = new URLSearchParams();
  if (classification && classification !== 'ALL') params.set('classification', classification);
  const qs = params.toString();
  const url = `${AGENT_URL}/sophos-email-sync/results${qs ? `?${qs}` : ''}`;
  const response = await fetch(url, { headers: getAgentAuthHeaders() });
  if (response.status === 404) {
    return { scan_run: null, results: [], total: 0 };
  }
  if (!response.ok) {
    const err = new Error(`Sophos Email Sync results request failed (${response.status})`);
    err.response = { status: response.status, data: await response.json().catch(() => ({})) };
    throw err;
  }
  return response.json();
}

async function triggerSophosEmailSyncScan() {
  const response = await fetch(`${AGENT_URL}/sophos-email-sync/trigger`, {
    method: 'POST',
    headers: getAgentAuthHeaders(),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const err = new Error(`Trigger Sophos Email Sync scan failed (${response.status})`);
    err.response = { status: response.status, data };
    throw err;
  }
  return response.json();
}

async function fetchSophosEmailSyncActiveRun() {
  const response = await fetch(`${AGENT_URL}/sophos-email-sync/runs/active`, {
    headers: getAgentAuthHeaders(),
  });
  if (response.status === 404) return null;
  if (!response.ok) return null;
  return response.json();
}

// ── Visual config ────────────────────────────────────────────────────────────

const CLASSIFICATION_PALETTE = {
  NO_SYNC: { color: 'var(--odin-text-secondary)',         label: 'NO SYNC' },
  ENTRA:   { color: 'var(--odin-accent)',                label: 'ENTRA' },
  AD:      { color: 'var(--odin-status-completed-text)', label: 'AD' },
  HYBRID:  { color: 'var(--odin-status-active-text)',    label: 'HYBRID' },
  UNKNOWN: { color: 'var(--odin-status-awaiting-text)',  label: 'UNKNOWN' },
};

function ClassificationBadge({ value }) {
  const cfg = CLASSIFICATION_PALETTE[value] || CLASSIFICATION_PALETTE.UNKNOWN;
  return (
    <Tag style={{
      background: `color-mix(in srgb, ${cfg.color} 16%, transparent)`, color: cfg.color,
      border: `1px solid color-mix(in srgb, ${cfg.color} 40%, transparent)`,
      fontWeight: 700, fontSize: 10, letterSpacing: '0.04em',
    }}>
      {cfg.label}
    </Tag>
  );
}

const COOLDOWN_MS = 30 * 60 * 1000;
function isCoolingDown(lastScanAt) {
  if (!lastScanAt) return false;
  return Date.now() - new Date(lastScanAt).getTime() < COOLDOWN_MS;
}

// ── Main component ───────────────────────────────────────────────────────────

export default function SophosEmailSyncReport() {
  const { hasRole } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = hasRole('Admin');
  const [classificationFilter, setClassificationFilter] = useState('ALL');
  const [hybridOnly, setHybridOnly] = useState(false);
  const [search, setSearch] = useState('');

  const effectiveFilter = hybridOnly ? 'HYBRID' : classificationFilter;

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['sophos-email-sync-results', effectiveFilter],
    queryFn: () => fetchSophosEmailSyncResults(effectiveFilter),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const { data: activeRun } = useQuery({
    queryKey: ['sophos-email-sync-active'],
    queryFn: fetchSophosEmailSyncActiveRun,
    refetchInterval: (q) => (q.state.data?.id ? 5000 : 60000),
    staleTime: 0,
    initialData: null,
  });

  // Tracks the wall-clock time of our last successful trigger. Used to tell
  // a real "scan completed" event from a silent fast-failure (typically
  // Sophos's upstream API rate-limiting us on the very first call, which can
  // kill a scan in <2s — far below the 60s idle polling cadence).
  const triggeredAtRef = useRef(null);

  const prevActiveId = useRef(undefined);
  useEffect(() => {
    if (prevActiveId.current !== undefined && prevActiveId.current !== null && !activeRun?.id) {
      // Scan transitioned from running → idle. Invalidate ALL filter
      // variants — same fix as DirectorySyncReport.jsx:150 — then decide
      // whether to show a success or warning toast based on whether
      // /results actually picked up a newer completed scan.
      queryClient.invalidateQueries({ queryKey: ['sophos-email-sync-results'] });
      const triggerTime = triggeredAtRef.current;
      triggeredAtRef.current = null;
      if (!triggerTime) {
        message.success('Sophos Email Sync scan complete — results updated.');
      } else {
        setTimeout(() => {
          const latest = queryClient.getQueryData(['sophos-email-sync-results', effectiveFilter]);
          const latestStart = latest?.scan_run?.started_at;
          const succeeded = latestStart && new Date(latestStart).getTime() >= triggerTime;
          if (succeeded) {
            message.success('Sophos Email Sync scan complete — results updated.');
          } else {
            message.warning('Scan ended without a completed result. Sophos may be rate-limiting — try again in a few minutes.');
          }
        }, 1500);
      }
    }
    prevActiveId.current = activeRun?.id ?? null;
  }, [activeRun?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerMutation = useMutation({
    mutationFn: triggerSophosEmailSyncScan,
    onSuccess: () => {
      triggeredAtRef.current = Date.now();
      message.success('Scan triggered. Progress will appear shortly.');
      // Refetch /runs/active immediately so a scan that runs and dies in
      // less than the 60s idle polling cycle still shows up briefly.
      queryClient.invalidateQueries({ queryKey: ['sophos-email-sync-active'] });

      // Safety net: scans that die in under a second (Sophos upstream 429
      // on the very first call) can complete entirely between polls. After
      // 10s, if we still haven't seen the running state, check /results to
      // see whether a fresh completed scan landed. If not, surface a
      // warning rather than leaving the user with no feedback.
      setTimeout(async () => {
        const triggerTime = triggeredAtRef.current;
        if (!triggerTime) return; // useEffect already handled completion
        const activeNow = queryClient.getQueryData(['sophos-email-sync-active']);
        if (activeNow?.id) return; // still running — normal flow will handle it
        await queryClient.refetchQueries({ queryKey: ['sophos-email-sync-results'] });
        const latest = queryClient.getQueryData(['sophos-email-sync-results', effectiveFilter]);
        const latestStart = latest?.scan_run?.started_at;
        const succeeded = latestStart && new Date(latestStart).getTime() >= triggerTime;
        triggeredAtRef.current = null;
        if (!succeeded) {
          message.warning('Scan ended without a completed result. Sophos may be rate-limiting — try again in a few minutes.');
        }
      }, 10000);
    },
    onError: (err) => {
      const status = err.response?.status;
      if (status === 429) {
        const retry = err.response?.data?.detail?.retryAfterSeconds ?? 0;
        message.warning(`Cooldown active — retry in ${Math.ceil(retry / 60)} minute(s).`);
      } else {
        message.error(`Trigger failed: ${err.message}`);
      }
    },
  });

  const scanRun = data?.scan_run;
  const results = data?.results || [];
  const filtered = results.filter((r) =>
    !search || (r.customer_name || '').toLowerCase().includes(search.toLowerCase()),
  );

  const counts = {
    NO_SYNC: scanRun?.no_sync_count ?? 0,
    ENTRA: scanRun?.entra_count ?? 0,
    AD: scanRun?.ad_count ?? 0,
    HYBRID: scanRun?.hybrid_count ?? 0,
  };

  const cooldown = isCoolingDown(scanRun?.completed_at || scanRun?.started_at);

  const numericSorter = (key) => (a, b) => (a[key] ?? 0) - (b[key] ?? 0);

  const columns = [
    {
      title: 'Customer',
      dataIndex: 'customer_name',
      key: 'customer_name',
      render: (v) => <Text strong style={{ color: 'var(--odin-text-primary)' }}>{v}</Text>,
      sorter: (a, b) => (a.customer_name || '').localeCompare(b.customer_name || ''),
    },
    {
      title: 'Classification',
      dataIndex: 'classification',
      key: 'classification',
      width: 130,
      render: (v) => <ClassificationBadge value={v} />,
      filters: Object.keys(CLASSIFICATION_PALETTE).map((k) => ({ text: k, value: k })),
      onFilter: (value, record) => record.classification === value,
    },
    {
      title: 'Entra Users',
      dataIndex: 'entra_user_count',
      key: 'entra_user_count',
      width: 110,
      align: 'right',
      sorter: numericSorter('entra_user_count'),
      render: (v) => v === null || v === undefined ? <Text type="secondary">—</Text> : <span>{v}</span>,
    },
    {
      title: 'AD Users',
      dataIndex: 'ad_user_count',
      key: 'ad_user_count',
      width: 100,
      align: 'right',
      sorter: numericSorter('ad_user_count'),
      render: (v) => v === null || v === undefined ? <Text type="secondary">—</Text> : <span>{v}</span>,
    },
    {
      title: 'Manual Users',
      dataIndex: 'manual_user_count',
      key: 'manual_user_count',
      width: 120,
      align: 'right',
      sorter: numericSorter('manual_user_count'),
      render: (v) => v === null || v === undefined
        ? <Text type="secondary">—</Text>
        : <Text type="secondary">{v}</Text>,
    },
    {
      title: 'Total Users',
      dataIndex: 'total_user_count',
      key: 'total_user_count',
      width: 110,
      align: 'right',
      sorter: numericSorter('total_user_count'),
      render: (v) => v === null || v === undefined ? <Text type="secondary">—</Text> : <span>{v}</span>,
    },
    {
      title: (
        <Tooltip title="Synced groups (Entra/AD/Google) / Total groups (incl. manual)">
          <span>Synced Groups</span>
        </Tooltip>
      ),
      dataIndex: 'synced_group_count',
      key: 'synced_group_count',
      width: 130,
      align: 'right',
      sorter: numericSorter('synced_group_count'),
      render: (v, record) => {
        if (v === null || v === undefined) return <Text type="secondary">—</Text>;
        const total = record.total_group_count ?? v;
        return <Tooltip title={`${v}/${total} groups synced`}><span>{v}</span></Tooltip>;
      },
    },
    {
      title: 'Mailboxes',
      dataIndex: 'mailboxes_licensed',
      key: 'mailboxes_licensed',
      width: 110,
      align: 'right',
      sorter: numericSorter('mailboxes_licensed'),
      render: (v) => v === null || v === undefined ? <Text type="secondary">—</Text> : <span>{v}</span>,
    },
    {
      title: 'Domain',
      dataIndex: 'domain',
      key: 'domain',
      ellipsis: true,
      render: (v) => <Text style={{ color: 'var(--odin-text-secondary)', fontFamily: 'monospace' }}>{v}</Text>,
    },
  ];

  if (isError) {
    return (
      <Alert type="error" message="Failed to load Sophos Email Sync results"
             description={error?.message || 'Unknown error'} showIcon />
    );
  }

  const noScanYet = !isPending && !scanRun;

  return (
    <div>
      {noScanYet && (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message="No Sophos Email Sync scan has run yet"
          description={
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text>
                {isAdmin
                  ? 'Click below to trigger your first scan. It takes ~5 minutes for the current estate.'
                  : 'Ask an admin to trigger the first scan.'}
              </Text>
              {isAdmin && (
                <Button
                  type="primary"
                  icon={<SyncOutlined spin={triggerMutation.isPending} />}
                  loading={triggerMutation.isPending}
                  onClick={() => triggerMutation.mutate()}
                  disabled={triggerMutation.isPending}
                >
                  Start First Scan
                </Button>
              )}
            </Space>
          }
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={6}>
          <Card size="small"><Statistic title="No Sync" value={counts.NO_SYNC}
            valueStyle={{ color: CLASSIFICATION_PALETTE.NO_SYNC.color }} /></Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small"><Statistic title="Entra" value={counts.ENTRA}
            valueStyle={{ color: CLASSIFICATION_PALETTE.ENTRA.color }} /></Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small"><Statistic title="AD" value={counts.AD}
            valueStyle={{ color: CLASSIFICATION_PALETTE.AD.color }} /></Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic title={`Hybrid · Total ${scanRun?.customer_count ?? 0}`}
              value={counts.HYBRID} valueStyle={{ color: CLASSIFICATION_PALETTE.HYBRID.color }} />
          </Card>
        </Col>
      </Row>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            value={classificationFilter}
            disabled={hybridOnly}
            onChange={setClassificationFilter}
            style={{ width: 200 }}
            options={[
              { value: 'ALL', label: 'All classifications' },
              { value: 'NO_SYNC', label: 'No Sync' },
              { value: 'ENTRA', label: 'Entra' },
              { value: 'AD', label: 'AD' },
              { value: 'HYBRID', label: 'Hybrid' },
              { value: 'UNKNOWN', label: 'Unknown' },
            ]}
          />
          <Switch
            checked={hybridOnly}
            onChange={setHybridOnly}
            checkedChildren="Hybrid only"
            unCheckedChildren="Hybrid only"
          />
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 220 }}
          />
          {isAdmin && (
            <Tooltip title={cooldown ? '30-minute cooldown active' : 'Run a fresh scan'}>
              <Button
                icon={<SyncOutlined spin={triggerMutation.isPending || !!activeRun?.id} />}
                onClick={() => triggerMutation.mutate()}
                disabled={cooldown || triggerMutation.isPending || !!activeRun?.id}
              >
                Refresh Now
              </Button>
            </Tooltip>
          )}
          <Button icon={<DownloadOutlined />} href={`${AGENT_URL}/sophos-email-sync/export/csv`}>
            Export CSV
          </Button>
          <Button icon={<FilePdfOutlined />} href={`${AGENT_URL}/sophos-email-sync/export/pdf`}>
            Export PDF
          </Button>
          {scanRun?.completed_at && (
            <Text type="secondary">
              Last scan: {new Date(scanRun.completed_at).toLocaleString()}
            </Text>
          )}
        </Space>
      </Card>

      {activeRun?.id && (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          message={`Scan in progress — ${activeRun.scanned_so_far ?? 0} / ${activeRun.total_expected ?? '?'} customers`}
          description={<Progress
            percent={
              activeRun.total_expected
                ? Math.round(((activeRun.scanned_so_far || 0) / activeRun.total_expected) * 100)
                : 0
            }
            status="active"
            strokeColor="var(--odin-accent)"
          />}
        />
      )}

      <Table
        size="small"
        loading={isPending}
        dataSource={filtered}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 50, showSizeChanger: true }}
      />
    </div>
  );
}
